import React, { createContext, useContext, useState, useCallback, ReactNode, useRef } from 'react';
import { gekkoApi } from '../utils/gekkoApi';
import type { Source } from '../types/api';

export interface SourcesContextValue {
  /** Get a source by ID, fetching it if not cached */
  ensureSource: (id: number) => Promise<Source>;
  /** Get multiple sources by IDs, fetching any not cached */
  ensureSources: (ids: number[]) => Promise<Source[]>;
  /** Get a cached source (returns undefined if not cached) */
  getCachedSource: (id: number) => Source | undefined;
}

const SourcesContext = createContext<SourcesContextValue | undefined>(undefined);

export const useSources = (): SourcesContextValue => {
  const context = useContext(SourcesContext);
  if (context === undefined) {
    throw new Error('useSources must be used within a SourcesProvider');
  }
  return context;
};

interface SourcesProviderProps {
  children: ReactNode;
}

export const SourcesProvider: React.FC<SourcesProviderProps> = ({ children }) => {
  const [cache, setCache] = useState<Map<number, Source>>(new Map());
  // Track in-flight requests to avoid duplicate fetches
  const pendingRequests = useRef<Map<number, Promise<Source>>>(new Map());

  const ensureSource = useCallback(async (id: number): Promise<Source> => {
    // Check cache first
    const cached = cache.get(id);
    if (cached) {
      return cached;
    }

    // Check if there's already a pending request for this ID
    const pending = pendingRequests.current.get(id);
    if (pending) {
      return pending;
    }

    // Fetch the source
    const fetchPromise = gekkoApi.getSource(id).then((source) => {
      setCache((prev) => {
        const next = new Map(prev);
        next.set(id, source);
        return next;
      });
      pendingRequests.current.delete(id);
      return source;
    }).catch((error) => {
      pendingRequests.current.delete(id);
      throw error;
    });

    pendingRequests.current.set(id, fetchPromise);
    return fetchPromise;
  }, [cache]);

  const ensureSources = useCallback(async (ids: number[]): Promise<Source[]> => {
    const results = await Promise.all(ids.map((id) => ensureSource(id)));
    return results;
  }, [ensureSource]);

  const getCachedSource = useCallback((id: number): Source | undefined => {
    return cache.get(id);
  }, [cache]);

  const value: SourcesContextValue = {
    ensureSource,
    ensureSources,
    getCachedSource,
  };

  return (
    <SourcesContext.Provider value={value}>
      {children}
    </SourcesContext.Provider>
  );
};
